import React, { useState } from 'react';
import { Input, Button, Checkbox, Divider } from '@nextui-org/react';
import { useAuth } from '../../hooks/useAuth';
import GoogleAuthButton from './GoogleAuthButton';

const LoginForm: React.FC = () => {
    const { login, error, loginWithGoogle, resetPassword } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showResetForm, setShowResetForm] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [resetEmail, setResetEmail] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await login(email, password, rememberMe);
        } catch (err) {
            // Error is handled in AuthContext
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        try {
            await loginWithGoogle();
        } catch (err) {
            // Error is handled in AuthContext
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await resetPassword(resetEmail);
            setShowSuccess(true);
        } catch (err) {
            // Error is handled in AuthContext
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setShowResetForm(false);
        setShowSuccess(false);
        setResetEmail('');
    };

    // Success view
    if (showSuccess) {
        return (
            <div className="flex flex-col gap-4 py-4">
                <div className="text-center">
                    <div className="text-green-500 text-6xl mb-4">✓</div>
                    <h3 className="text-lg font-semibold mb-2">Check your email</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        We've sent a password reset link to <strong>{resetEmail}</strong>
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        If an account exists with this email, you'll receive a reset link shortly.
                    </p>
                </div>

                <Button
                    color="primary"
                    variant="solid"
                    fullWidth
                    onClick={handleBackToLogin}
                    className="bg-blue-500 text-white"
                >
                    Back to Login
                </Button>
            </div>
        );
    }

    // Reset password form
    if (showResetForm) {
        return (
            <form onSubmit={handleResetPassword} className="flex flex-col gap-4 py-4">
                <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold mb-2">Reset Password</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>
                </div>

                <Input
                    label="Email"
                    type="email"
                    placeholder="Enter your email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    variant="bordered"
                    isRequired
                    autoComplete="email"
                    classNames={{
                        label: "text-black dark:text-white",
                        input: "text-black dark:text-white",
                    }}
                />

                <p className="text-sm text-gray-500 text-center">
                    If an account exists with this email, a reset link will be sent.
                </p>

                {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                )}

                <Button
                    type="submit"
                    color="primary"
                    variant="solid"
                    fullWidth
                    isLoading={isLoading}
                    className="bg-blue-500 text-white"
                >
                    Send Reset Link
                </Button>

                <Button
                    color="default"
                    variant="light"
                    fullWidth
                    onClick={handleBackToLogin}
                >
                    Back to Login
                </Button>
            </form>
        );
    }

    // Login form
    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 py-4">
            <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="bordered"
                isRequired
                autoComplete="email"
                classNames={{
                    label: "text-black dark:text-white",
                    input: "text-black dark:text-white",
                }}
            />

            <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="bordered"
                isRequired
                autoComplete="current-password"
                classNames={{
                    label: "text-black dark:text-white",
                    input: "text-black dark:text-white",
                }}
            />

            <div className="flex justify-between items-center">
                <Checkbox
                    isSelected={rememberMe}
                    onValueChange={setRememberMe}
                    size="sm"
                >
                    Remember me
                </Checkbox>
                <button
                    type="button"
                    onClick={() => {
                        setResetEmail(email);
                        setShowResetForm(true);
                    }}
                    className="text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    Forgot Password?
                </button>
            </div>

            {error && (
                <p className="text-red-500 text-sm">{error}</p>
            )}

            <Button
                type="submit"
                color="primary"
                variant="solid"
                fullWidth
                isLoading={isLoading}
                className="bg-blue-500 text-white"
            >
                Login
                <svg
                    className="ml-2 w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
            </Button>

            <div className="relative">
                <Divider className="my-4" />
                <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 px-2 text-sm text-gray-500">
                    OR
                </span>
            </div>

            <GoogleAuthButton onClick={handleGoogleLogin} isLoading={isLoading} />
        </form>
    );
};

export default LoginForm; 